// Hermes → Nexus route-request — dashboard render + lifecycle helper.
//
// Council-decision (decisionType='hermes.route_request') için typed görünüm.
// Lifecycle mapping (council status + context.lifecycle → kullanıcı durumu):
//   open  + context.lifecycle=submitted      → submitted
//   open  + context.lifecycle=needs_approval → needs_approval (Yılmaz gate)
//   open  + context.seenBy varsa             → seen
//   approved                                 → accepted
//   rejected / expired                       → blocked
//
// Hermes propose-only — bu kayıt bir öneridir; Nexus/council resolve eder.
// Bkz. wiki/decisions/2026-06-14-hermes-nexus-route-request.md

import type { CouncilDecision } from '../views/agent-council.js';

export type RouteRequestLifecycle =
  | 'submitted'
  | 'seen'
  | 'needs_approval'
  | 'accepted'
  | 'blocked'
  | 'unknown';

export function isRouteRequest(d: CouncilDecision): boolean {
  return d.decisionType === 'hermes.route_request';
}

function ctx(d: CouncilDecision): Record<string, unknown> {
  return (d.context && typeof d.context === 'object' ? d.context : {}) as Record<string, unknown>;
}

export function routeRequestLifecycle(d: CouncilDecision): RouteRequestLifecycle {
  if (d.status === 'approved') return 'accepted';
  if (d.status === 'rejected' || d.status === 'expired') return 'blocked';
  if (d.status === 'open') {
    const c = ctx(d);
    if (c.lifecycle === 'needs_approval' || c.requiresYilmazApproval === true) return 'needs_approval';
    if (c.seenBy) return 'seen';
    return 'submitted';
  }
  return 'unknown';
}

export const LIFECYCLE_LABEL: Record<RouteRequestLifecycle, string> = {
  submitted: '🟠 Gönderildi (Nexus bekliyor)',
  seen: '🔵 Görüldü',
  needs_approval: '🟡 Yılmaz onayı gerekli',
  accepted: '🟢 Kabul edildi',
  blocked: '🔴 Bloklandı',
  unknown: '⚠️ Bilinmeyen',
};

export const LIFECYCLE_TONE: Record<RouteRequestLifecycle, string> = {
  submitted: '#fbbf24',
  seen: '#9bd0ff',
  needs_approval: '#fde047',
  accepted: '#9be8a8',
  blocked: '#f0a8a8',
  unknown: '#94a3b8',
};

// Context accessor'ları (typed alanlar)
export interface RouteRequestFields {
  targetAgent: string;
  requestedAction: string;
  evidence: string[];
  correlationId: string;
  approvalPolicy: string;
}
export function routeRequestFields(d: CouncilDecision): RouteRequestFields {
  const c = ctx(d);
  const ev = Array.isArray(c.evidence) ? (c.evidence as unknown[]).map((x) => String(x)) : c.evidence ? [String(c.evidence)] : [];
  return {
    targetAgent: typeof c.targetAgent === 'string' ? c.targetAgent : 'nexus',
    requestedAction: typeof c.requestedAction === 'string' ? c.requestedAction : '',
    evidence: ev,
    correlationId: typeof c.correlationId === 'string' ? c.correlationId : '',
    approvalPolicy: typeof c.approvalPolicy === 'string' ? c.approvalPolicy : '',
  };
}
